"use client";

import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { useEffect, useState } from "react";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Link as LinkIcon,
  Home,
} from "lucide-react";
import { RecommendedProperties } from "./tiptap-extensions/recommended-properties";
import { PropertySelectorDialog } from "./property-selector-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface TiptapEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

export function TiptapEditor({
  content,
  onChange,
  placeholder = "Start typing...",
  className,
  minHeight = "200px",
}: TiptapEditorProps) {
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showPropertySelector, setShowPropertySelector] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");

  // process HTML to preserve whitespace and line breaks
  // use ProseMirror JSON to preserve whitespace, then serialize to HTML with spaces preserved
  const processHtmlForWhitespace = (editor: Editor): string => {
    try {
      // get the ProseMirror JSON document which preserves whitespace
      const json = editor.getJSON();

      // serialize JSON to HTML while preserving whitespace
      const serializeNode = (node: Record<string, unknown>): string => {
        const type = node.type as string;
        const attrs = (node.attrs || {}) as Record<string, unknown>;
        const content = (node.content || []) as Array<Record<string, unknown>>;
        const marks = (node.marks || []) as Array<Record<string, unknown>>;
        const text = node.text as string | undefined;

        if (text !== undefined) {
          // preserve multiple spaces by converting to non-breaking spaces
          let processedText = text;
          // replace 2+ consecutive spaces with non-breaking spaces (except last one)
          processedText = processedText.replace(/ {2,}/g, (match) => {
            return "\u00A0".repeat(match.length - 1) + " ";
          });

          // apply marks
          let wrappedText = processedText;
          for (const mark of marks.reverse()) {
            const markType = mark.type as string;
            const markAttrs = (mark.attrs || {}) as Record<string, unknown>;

            if (markType === "bold") {
              wrappedText = `<strong>${wrappedText}</strong>`;
            } else if (markType === "italic") {
              wrappedText = `<em>${wrappedText}</em>`;
            } else if (markType === "link") {
              const href = (markAttrs.href as string) || "";
              const title = (markAttrs.title as string) || "";
              const titleAttr = title
                ? ` title="${title.replace(/"/g, "&quot;")}"`
                : "";
              wrappedText = `<a href="${href.replace(/"/g, "&quot;")}"${titleAttr}>${wrappedText}</a>`;
            }
          }

          return wrappedText;
        }

        let html = "";
        for (const child of content) {
          html += serializeNode(child);
        }

        let tag = "";
        let attributes = "";

        if (type === "paragraph") {
          tag = "p";
        } else if (type === "heading") {
          const level = (attrs.level as number) || 1;
          tag = `h${level}`;
        } else if (type === "bulletList") {
          tag = "ul";
        } else if (type === "orderedList") {
          tag = "ol";
        } else if (type === "listItem") {
          tag = "li";
        } else if (type === "blockquote") {
          tag = "blockquote";
        } else if (type === "hardBreak") {
          return "<br>";
        } else if (type === "image") {
          const src = (attrs.src as string) || "";
          const alt = (attrs.alt as string) || "";
          return `<img src="${src.replace(/"/g, "&quot;")}" alt="${alt.replace(/"/g, "&quot;")}" />`;
        } else if (type === "recommendedProperties") {
          const propertyIds = (attrs.propertyIds as string[]) || [];
          const idsAttr =
            propertyIds.length > 0
              ? ` data-property-ids="${propertyIds.join(",").replace(/"/g, "&quot;")}"`
              : "";
          return `<div data-type="recommended-properties"${idsAttr}></div>`;
        }

        if (!tag) return html;

        return `<${tag}${attributes}>${html}</${tag}>`;
      };

      let result = "";
      if (json.content && Array.isArray(json.content)) {
        for (const node of json.content) {
          result += serializeNode(node);
        }
      }

      return result;
    } catch (error) {
      console.error("Error processing HTML for whitespace:", error);
      // fallback to regular HTML if JSON processing fails
      return editor.getHTML();
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: "max-w-full h-auto",
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 underline",
        },
      }),
      RecommendedProperties.configure({
        HTMLAttributes: {
          class: "recommended-properties-placeholder",
        },
      }),
    ],
    content: content || "",
    onUpdate: ({ editor }) => {
      const processedHtml = processHtmlForWhitespace(editor);
      onChange(processedHtml);
    },
    editorProps: {
      attributes: {
        class: cn(
          "p-4 border-0 bg-transparent focus:outline-none max-w-full overflow-wrap-break-word break-words",
          "[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:break-words",
          "[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mb-3 [&_h2]:break-words",
          "[&_h3]:text-lg [&_h3]:font-medium [&_h3]:mb-2 [&_h3]:break-words",
          "[&_p]:mb-3 [&_p]:leading-relaxed [&_p]:break-words [&_p]:whitespace-pre-wrap",
          "[&_ul]:list-disc [&_ul]:list-outside [&_ul]:mb-3 [&_ul]:pl-6",
          "[&_ol]:list-decimal [&_ol]:list-outside [&_ol]:mb-3 [&_ol]:pl-6",
          "[&_li]:mb-1 [&_li]:leading-normal",
          "[&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:mb-3",
          "[&_strong]:font-semibold",
          "[&_em]:italic",
          "[&_img]:max-w-full [&_img]:h-auto [&_img]:block",
          "[&_a]:break-all",
          "[&_.recommended-properties-placeholder]:my-4 [&_.recommended-properties-placeholder]:border-2 [&_.recommended-properties-placeholder]:border-dashed [&_.recommended-properties-placeholder]:border-blue-300 [&_.recommended-properties-placeholder]:bg-blue-50 [&_.recommended-properties-placeholder]:rounded-lg [&_.recommended-properties-placeholder]:p-4 [&_.recommended-properties-placeholder]:cursor-default [&_.recommended-properties-placeholder]:select-none",
          "[&_.recommended-properties-placeholder-content]:flex [&_.recommended-properties-placeholder-content]:items-center [&_.recommended-properties-placeholder-content]:gap-2",
          "[&_.recommended-properties-icon]:text-xl",
          "[&_.recommended-properties-text]:font-semibold [&_.recommended-properties-text]:text-blue-700",
          "[&_.recommended-properties-count]:text-sm [&_.recommended-properties-count]:text-blue-600",
          className
        ),
        style: `min-height: ${minHeight}; word-break: break-word; overflow-wrap: break-word;`,
      },
    },
  });

  // update editor content when content prop changes (for editing existing properties)
  // but only on initial load, not during editing to preserve focus
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentContent = editor.getHTML();
      const newContent = content || "";

      // only update if content is significantly different (initial load)
      // and the editor doesn't currently have focus to avoid disrupting typing
      if (
        newContent !== currentContent &&
        !editor.isFocused &&
        (currentContent === "<p></p>" || currentContent === "")
      ) {
        // set content directly - the HTML already has non-breaking spaces preserved
        editor.commands.setContent(newContent);
      }
    }
  }, [editor, content]);

  const handleInsertImage = () => {
    if (imageUrl.trim()) {
      editor
        .chain()
        .focus()
        .setImage({ src: imageUrl.trim(), alt: imageAlt.trim() || "" })
        .run();
      setImageUrl("");
      setImageAlt("");
      setShowImageDialog(false);
    }
  };

  const handleSetLink = () => {
    if (linkUrl.trim()) {
      const attrs: { href: string; title?: string } = {
        href: linkUrl.trim(),
      };
      if (linkTitle.trim()) {
        attrs.title = linkTitle.trim();
      }
      editor.chain().focus().setLink(attrs).run();
      setLinkUrl("");
      setLinkTitle("");
      setShowLinkDialog(false);
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <>
      <div className="border border-input rounded-md bg-background overflow-hidden">
        {/* toolbar */}
        <div className="border-b border-input p-2 flex flex-wrap gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            className={cn(
              "h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer",
              editor.isActive("bold") && "bg-accent text-accent-foreground"
            )}
          >
            <Bold className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            className={cn(
              "h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer",
              editor.isActive("italic") && "bg-accent text-accent-foreground"
            )}
          >
            <Italic className="h-4 w-4" />
          </Button>

          <div className="w-px h-6 bg-border mx-1" />

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            className={cn(
              "h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer",
              editor.isActive("heading", { level: 1 }) &&
                "bg-accent text-accent-foreground"
            )}
          >
            <Heading1 className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            className={cn(
              "h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer",
              editor.isActive("heading", { level: 2 }) &&
                "bg-accent text-accent-foreground"
            )}
          >
            <Heading2 className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            className={cn(
              "h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer",
              editor.isActive("heading", { level: 3 }) &&
                "bg-accent text-accent-foreground"
            )}
          >
            <Heading3 className="h-4 w-4" />
          </Button>

          <div className="w-px h-6 bg-border mx-1" />

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={cn(
              "h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer",
              editor.isActive("bulletList") &&
                "bg-accent text-accent-foreground"
            )}
          >
            <List className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={cn(
              "h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer",
              editor.isActive("orderedList") &&
                "bg-accent text-accent-foreground"
            )}
          >
            <ListOrdered className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={cn(
              "h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer",
              editor.isActive("blockquote") &&
                "bg-accent text-accent-foreground"
            )}
          >
            <Quote className="h-4 w-4" />
          </Button>

          <div className="w-px h-6 bg-border mx-1" />

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowImageDialog(true)}
            className="h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
          >
            <ImageIcon className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              if (editor.isActive("link")) {
                const attrs = editor.getAttributes("link");
                setLinkUrl(attrs.href || "");
                setLinkTitle(attrs.title || "");
                setShowLinkDialog(true);
              } else {
                const { from, to } = editor.state.selection;
                const text = editor.state.doc.textBetween(from, to, " ");
                setLinkUrl(text);
                setLinkTitle("");
                setShowLinkDialog(true);
              }
            }}
            className={cn(
              "h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer",
              editor.isActive("link") && "bg-accent text-accent-foreground"
            )}
          >
            <LinkIcon className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowPropertySelector(true)}
            className="h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
            title="Insert Recommended Properties"
          >
            <Home className="h-4 w-4" />
          </Button>

          <div className="w-px h-6 bg-border mx-1" />

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run()}
            className="h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
          >
            <Undo className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run()}
            className="h-8 px-2 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
          >
            <Redo className="h-4 w-4" />
          </Button>
        </div>

        {/* editor content */}
        <div className="relative overflow-x-auto max-w-full">
          {(!content || content.trim() === "" || content === "<p></p>") && (
            <div className="absolute top-4 left-4 text-muted-foreground pointer-events-none">
              {placeholder}
            </div>
          )}
          <EditorContent editor={editor} />
        </div>
      </div>

      {/* image dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="image-url">Image URL *</Label>
              <Input
                id="image-url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="image-alt">Alt Text</Label>
              <Input
                id="image-alt"
                value={imageAlt}
                onChange={(e) => setImageAlt(e.target.value)}
                placeholder="Description of the image"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowImageDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleInsertImage}
              disabled={!imageUrl.trim()}
            >
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* link dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editor.isActive("link") ? "Edit Link" : "Insert Link"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="link-url">URL *</Label>
              <Input
                id="link-url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="link-title">Title (Tooltip)</Label>
              <Input
                id="link-title"
                value={linkTitle}
                onChange={(e) => setLinkTitle(e.target.value)}
                placeholder="Link description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowLinkDialog(false);
                setLinkUrl("");
                setLinkTitle("");
              }}
            >
              Cancel
            </Button>
            {editor.isActive("link") && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  editor.chain().focus().unsetLink().run();
                  setLinkUrl("");
                  setLinkTitle("");
                  setShowLinkDialog(false);
                }}
              >
                Remove Link
              </Button>
            )}
            <Button
              type="button"
              onClick={handleSetLink}
              disabled={!linkUrl.trim()}
            >
              {editor.isActive("link") ? "Update" : "Insert"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* property selector dialog */}
      <PropertySelectorDialog
        open={showPropertySelector}
        onOpenChange={setShowPropertySelector}
        onSelect={(propertyIds) => {
          if (propertyIds.length > 0) {
            editor
              .chain()
              .focus()
              .setRecommendedProperties({ propertyIds })
              .run();
          }
        }}
      />
    </>
  );
}
